import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Stage, Layer, Circle, Line, Text, Arc, Arrow, Label, Tag, Group } from "react-konva";

function round(value, precision = 0) {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

function normalize(val, max, min) {
  if (max - min === 0) return 15;
  return 15 + (((val - min) * (85 - 15)) / (max - min));
}

function Enneagram({ settings, width = 1000, height = 1000 }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [hoveredType, setHoveredType] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
    pointerDirection: "up",
  });

  const defaultOptions = {
    radius: width / 3,
    backgroundCircleRadius: 60,
    typeCircleRadius: 50,
    maxColor: [236, 76, 60],
    minColor: [52, 73, 94],
    lineWidth: 3,
    lineColor: "rgb(62, 83, 104)",
    pointSize: 75,
    pointFontSize: 19,
    pointLineHeight: 23,
    fontColor: "black",
  };

  const opt = { ...defaultOptions, ...(settings.options || {}) };
  const centerX = width / 2;
  const centerY = height / 2;
  const angleIncr = 360 / 9;
  const zodiacAngle = -(2 * Math.PI) / 12;
  const decanAngle = (2 * Math.PI) / 36;

  const typeLabels = [
    "Perfection Seeking",
    "Seeking to feel emotions",
    "Admirable Self Image Seeking",
    "Seeking Meaning of Emotions",
    "Seeking Meaning of Knowledge",
    "Intellectual Serenity Seeking",
    "Seeking the Joy of Discovery",
    "Absolute Power Seeking",
    "Sensory Comfort Seeking",
  ];

  const desintegrationPairs = [
    [1, 4],
    [2, 8],
    [3, 9],
    [4, 2],
    [5, 7],
    [6, 3],
    [7, 1],
    [8, 5],
    [9, 6],
  ];
  const integrationPairs = [
    [1, 7],
    [2, 4],
    [3, 6],
    [4, 1],
    [5, 8],
    [6, 9],
    [7, 5],
    [8, 2],
    [9, 3],
  ];

  const typeColors = [
    "#f97800",
    "#6c2185",
    "#f6c22f",
    "#75ad49",
    "#820d46",
    "#1d5280",
    "#c92135",
    "#1d7139",
    "#3ab49e",
  ];

  const zodiacSigns = [
    "Capricorn",
    "Aquarius",
    "Pisces",
    "Aries",
    "Taurus",
    "Gemini",
    "Cancer",
    "Leo",
    "Virgo",
    "Libra",
    "Scorpio",
    "Sagittarius",
  ];

  // Unicode glyphs avoid requiring the HamburgSymbols font.
  const zodiacGlyphs = ["♑", "♒", "♓", "♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐"];

  const maxHarmony = useMemo(() => Math.max(...settings.harmony.map(Math.abs)), [settings.harmony]);
  const minHarmony = useMemo(() => Math.min(...settings.harmony.map(Math.abs)), [settings.harmony]);

  const getTypePosition = (index) => {
    const x = centerX + opt.radius * Math.cos(-(0 - index * angleIncr + 50) * (Math.PI / 180));
    const y = centerY + opt.radius * Math.sin(-(0 - index * angleIncr + 50) * (Math.PI / 180));
    return { x, y };
  };

  const getZodiacPosition = (index) => {
    const sectionStartAngle = index * zodiacAngle;
    const sectionEndAngle = (index + 1) * zodiacAngle;
    const sectionMiddleAngle = (sectionStartAngle + sectionEndAngle) / 2;
    const sectionRadius = opt.radius + 75;
    const x = centerX + sectionRadius * Math.cos(sectionMiddleAngle);
    const y = centerY + sectionRadius * Math.sin(sectionMiddleAngle);
    return { x, y };
  };

  const getPairIndexesForType = (typeId) => {
    const indexes = [];
    desintegrationPairs.forEach((pair, idx) => {
      if (pair[0] === typeId || pair[1] === typeId) indexes.push(idx);
    });
    return indexes;
  };

  useEffect(() => {
    const fit = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth || width;
      setScale(containerWidth / width);
    };

    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [width]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Stage width={width * scale} height={height * scale} scaleX={scale} scaleY={scale}>
        <Layer>
          <Circle x={centerX} y={centerY} radius={opt.radius + 100} fill="orange" stroke="black" strokeWidth={3} />
          <Circle x={centerX} y={centerY} radius={opt.radius + 50} fill="black" stroke="black" strokeWidth={3} />

          {zodiacSigns.map((_, i) => {
            const pos = getZodiacPosition(i);
            return (
              <Text
                key={`zodiac-sign-${i}`}
                x={pos.x}
                y={pos.y}
                text={zodiacGlyphs[i]}
                fontSize={32}
                align="center"
                fontStyle="bold"
                fill="black"
                offsetX={16}
                offsetY={16}
                onMouseEnter={() => {
                  const yOffset = [0, 1, 6, 7, 8].includes(i) ? -10 : 10;
                  const pointerDirection = [0, 1, 6, 7, 8].includes(i) ? "down" : "up";
                  setTooltip({
                    visible: true,
                    x: pos.x,
                    y: pos.y + yOffset,
                    text: zodiacSigns[i],
                    pointerDirection,
                  });
                }}
                onMouseLeave={() =>
                  setTooltip({ visible: false, x: 0, y: 0, text: "", pointerDirection: "up" })
                }
              />
            );
          })}

          {Array.from({ length: 12 }, (_, i) => (
            <Line
              key={`zodiac-divider-${i}`}
              points={[
                centerX + (opt.radius + 50) * Math.cos(i * zodiacAngle),
                centerY + (opt.radius + 50) * Math.sin(i * zodiacAngle),
                centerX + (opt.radius + 100) * Math.cos(i * zodiacAngle),
                centerY + (opt.radius + 100) * Math.sin(i * zodiacAngle),
              ]}
              stroke="black"
              strokeWidth={3}
            />
          ))}
        </Layer>

        <Layer>
          <Circle x={centerX} y={centerY} radius={opt.radius} fill="white" stroke="black" strokeWidth={3} />

          {desintegrationPairs.map((pair, index) => {
            const x1 = centerX + opt.radius * Math.cos(-(90 - angleIncr * pair[0]) * (Math.PI / 180));
            const y1 = centerY + opt.radius * Math.sin(-(90 - angleIncr * pair[0]) * (Math.PI / 180));
            const x2 = centerX + opt.radius * Math.cos(-(90 - angleIncr * pair[1]) * (Math.PI / 180));
            const y2 = centerY + opt.radius * Math.sin(-(90 - angleIncr * pair[1]) * (Math.PI / 180));

            const pairIndexes = hoveredType !== null ? getPairIndexesForType(hoveredType + 1) : [];
            const highlighted = pairIndexes.includes(index);

            return (
              <Line
                key={`pair-${index}`}
                points={[x1, y1, x2, y2]}
                stroke="black"
                strokeWidth={3}
                shadowEnabled={highlighted}
                shadowColor="black"
                shadowBlur={10}
                shadowOffsetX={10}
                shadowOffsetY={10}
                shadowOpacity={0.5}
              />
            );
          })}

          {settings.harmony.map((h, i) => {
            const pair = h > 0 ? integrationPairs[i] : desintegrationPairs[i];
            const normalizedHarmony = normalize(Math.abs(h), maxHarmony, minHarmony);
            const x1 = centerX + opt.radius * Math.cos(-(90 - angleIncr * pair[0]) * (Math.PI / 180));
            const y1 = centerY + opt.radius * Math.sin(-(90 - angleIncr * pair[0]) * (Math.PI / 180));
            const x2 = centerX + opt.radius * Math.cos(-(90 - angleIncr * pair[1]) * (Math.PI / 180));
            const y2 = centerY + opt.radius * Math.sin(-(90 - angleIncr * pair[1]) * (Math.PI / 180));
            const x3 = x1 + (normalizedHarmony / 100) * (x2 - x1);
            const y3 = y1 + (normalizedHarmony / 100) * (y2 - y1);

            return (
              <Arrow
                key={`harmony-arrow-${i}`}
                points={[x1, y1, x3, y3]}
                stroke="black"
                strokeWidth={3}
                pointerLength={20}
                pointerWidth={20}
                fill={h > 0 ? "#75ad49" : "#c92135"}
              />
            );
          })}
        </Layer>

        <Layer>
          {Array.from({ length: 9 }, (_, i) => {
            const pos = getTypePosition(i);
            const power = settings.power[i] > 100 ? 100 : settings.power[i];
            const angle = (power * 360) / 100;
            const yOffset = [0, 1, 6, 7, 8].includes(i) ? -opt.backgroundCircleRadius : opt.backgroundCircleRadius;
            const pointerDirection = [0, 1, 6, 7, 8].includes(i) ? "down" : "up";

            return (
              <Group key={`type-group-${i}`}>
                <Circle
                  x={pos.x}
                  y={pos.y}
                  radius={opt.backgroundCircleRadius}
                  fill="white"
                  stroke="black"
                  strokeWidth={1}
                  shadowColor="black"
                  shadowBlur={10}
                  shadowOffset={{ x: 10, y: 10 }}
                  shadowOpacity={0.5}
                />
                <Circle
                  x={pos.x}
                  y={pos.y}
                  radius={opt.typeCircleRadius}
                  fill="white"
                  stroke="black"
                  strokeWidth={1}
                />
                <Text
                  x={pos.x}
                  y={pos.y}
                  text={String(i + 1)}
                  fontFamily="Arial"
                  fontSize={25}
                  fill={typeColors[i]}
                  width={opt.typeCircleRadius * 2}
                  align="center"
                  offsetX={opt.typeCircleRadius}
                  offsetY={opt.typeCircleRadius / 3.5}
                  fontStyle="bold"
                  onMouseEnter={() => {
                    setHoveredType(i);
                    setTooltip({
                      visible: true,
                      x: pos.x,
                      y: pos.y + yOffset,
                      text: `${typeLabels[i]}\nPower: ${round(settings.power[i], 1)}\nHarmony: ${round(
                        settings.harmony[i],
                        1
                      )}`,
                      pointerDirection,
                    });
                  }}
                  onMouseLeave={() => {
                    setHoveredType(null);
                    setTooltip({ visible: false, x: 0, y: 0, text: "", pointerDirection: "up" });
                  }}
                />
                <Arc
                  x={pos.x}
                  y={pos.y}
                  innerRadius={opt.typeCircleRadius}
                  outerRadius={opt.backgroundCircleRadius}
                  angle={angle}
                  fill={typeColors[i]}
                  stroke="black"
                  strokeWidth={1}
                />
              </Group>
            );
          })}
        </Layer>

        <Layer>
          {tooltip.visible && (
            <Label x={tooltip.x} y={tooltip.y} opacity={0.75} listening={false}>
              <Tag
                fill="black"
                pointerDirection={tooltip.pointerDirection}
                cornerRadius={5}
                pointerWidth={10}
                pointerHeight={10}
                lineJoin="round"
                shadowColor="black"
                shadowBlur={10}
                shadowOffsetX={10}
                shadowOffsetY={10}
                shadowOpacity={0.2}
              />
              <Text
                text={tooltip.text}
                fontFamily="Arial"
                fontSize={16}
                fontStyle="bold"
                padding={6}
                fill="white"
              />
            </Label>
          )}
        </Layer>
      </Stage>
    </div>
  );
}

Enneagram.propTypes = {
  settings: PropTypes.shape({
    power: PropTypes.arrayOf(PropTypes.number).isRequired,
    harmony: PropTypes.arrayOf(PropTypes.number).isRequired,
    options: PropTypes.object,
  }).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default Enneagram;
